import apiClient from "@/api-client";
import { toast } from "@/components/core/toaster";
import { logger } from "@/lib/default-logger";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { campaignsKeys } from "./use-campaigns";

export interface Tool {
	name: string;
	description: string;
  title: string;
	fields: Record<string, any>;
  llm: {
    name: string;
    description: string;
    parameters: any;
  }
}

export interface CampaignTool {
	name: string;
	description: string;
	allowInterruption: boolean;
	enabled: boolean;
	settings: Record<string, any>;
}

export const toolsKeys = {
  all: ['tools'] as const,
  campaignTools: (campaignId: string) => [...toolsKeys.all, campaignId] as const,
};

export function useTools() {
  return useQuery({
    queryKey: toolsKeys.all,
    queryFn: async () => {
      const response = await apiClient.get(`/tools`);
      return response.data;
    },
  });
}

export function useCampaignTools(campaignId: string) {
  return useQuery({
    queryKey: toolsKeys.campaignTools(campaignId),
    queryFn: async () => {
      const response = await apiClient.get(`/campaigns/${campaignId}/tools?offset=0&limit=100`);
      return response.data.data;
    },
    enabled: !!campaignId,
  });
}

export function useUpdateCampaignTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ campaignId, name, data }: { campaignId: string; name: string; data: CampaignTool }): Promise<CampaignTool> => {
      const response = await apiClient.put<CampaignTool>(`/campaigns/${campaignId}/tools/${name}`, data);
      return response.data;
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: toolsKeys.campaignTools(variables.campaignId) });
      await queryClient.invalidateQueries({ queryKey: campaignsKeys.detail(variables.campaignId) });
      await queryClient.invalidateQueries({ queryKey: campaignsKeys.summarySchema(variables.campaignId) });
      toast.success('Tool updated successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'Something went wrong!');
    },
  });
}